import { Inject, Injectable } from '@angular/core';
import { LogInstanceToken, LogInterface, PaceInsightsService } from '@evorait/pace-logging';
import { EnvironmentService } from '@evorait/pace-environment';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { BsModalService } from 'ngx-bootstrap/modal';
import { Observable, of } from 'rxjs';
import { catchError, filter, map, switchMap } from 'rxjs/operators';
import { PacePartnerZgidErrorModalComponent } from '../../../components/pace-partner-zgid-error-modal/pace-partner-zgid-error-modal.component';
import { ENDPOINTS } from '../../../enums/endpoints.enum';
import { ModalClass } from '../../../enums/modal-bootstrap-classes.enum';
import { ClientManagerInstanceToken, ClientManagerInterface } from '../../../interfaces/client-manager.interface';
import { ODataUserModel } from '../../../models/complex/user.model';
import { AppNotificationType } from '../../../services/app-notification/app-notification.model';
import { AppNotificationService } from '../../../services/app-notification/app-notification.service';
import { OnlineOnlyService } from '../../../services/online-only/online-only.service';
import { hydrateSlice, persistSlice, sliceHydrated } from '../../persistence';
import {
    initUserMaster,
    initUserMasterFailed,
    loadUserMasterFromBackend,
    userMasterRestored,
    userSelected,
} from '../actions/user-master.actions';
import { selectUserMasterLoaded } from '../selectors/user-master.selector';
import { USER_MASTER_FEATURE_KEY, UserMasterState } from '../user-master-collection.state';
import { isPaceOnlineUserFrontId } from '../user-master.utils';

/**
 * Owns the user-master resolution flow: rehydrate the persisted selection from the unified
 * slice storage, or load the single sales-org/plant the backend returns, validate it, enforce
 * the partner/internal hard-fail gate, and announce the result. The NgRx store is the source
 * of truth: `userSelected` triggers a generic `persistSlice`; `hydrateUserMasterOnInit$`
 * reads the cache back at startup; both hydrate steps are guarded on
 * {@link selectUserMasterLoaded} so a slower cache read can never overwrite fresher data.
 * When nothing is cached, the backend-resolution path runs instead.
 */
@Injectable()
export class UserMasterEffects {
    public constructor(
        private readonly actions$: Actions,
        private readonly store: Store,
        private readonly onlineOnlyService: OnlineOnlyService,
        private readonly modalService: BsModalService,
        private readonly appNotificationService: AppNotificationService,
        private readonly translate: TranslateService,
        private readonly environmentService: EnvironmentService,
        private readonly paceInsights: PaceInsightsService,
        @Inject(ClientManagerInstanceToken) private readonly clientManager: ClientManagerInterface,
        @Inject(LogInstanceToken) private readonly log: LogInterface,
    ) {}

    /** Trigger hydration from generic persistence on init, guarded by loaded status. */
    public hydrateUserMasterOnInit$ = createEffect(() =>
        this.actions$.pipe(
            ofType(initUserMaster),
            concatLatestFrom(() => this.store.select(selectUserMasterLoaded)),
            filter(([, loaded]) => !loaded),
            map(() => hydrateSlice({ featureKey: USER_MASTER_FEATURE_KEY })),
        ),
    );

    /**
     * Bridge the generic hydrate result into the user-master slice, unless the store already holds a
     * user. Stored data is restored directly; with nothing stored, the selection is resolved from the
     * backend instead.
     */
    public applyHydratedOrResolveUserMaster$ = createEffect(() =>
        this.actions$.pipe(
            ofType(sliceHydrated),
            filter(({ featureKey }) => featureKey === USER_MASTER_FEATURE_KEY),
            concatLatestFrom(() => this.store.select(selectUserMasterLoaded)),
            filter(([, loaded]) => !loaded),
            map(([{ data }]) => {
                if (data !== undefined) {
                    const user = data as UserMasterState;
                    this.applySelectionSideEffects(user);
                    return userMasterRestored({ user });
                }
                return loadUserMasterFromBackend();
            }),
        ),
    );

    /** Persist the selection once it settles in the slice — the store is the source of truth. */
    public persistSelectedUser$ = createEffect(() =>
        this.actions$.pipe(
            ofType(userSelected),
            map(() => persistSlice({ featureKey: USER_MASTER_FEATURE_KEY })),
        ),
    );

    public loadUserMasterFromBackend$ = createEffect(() =>
        this.actions$.pipe(
            ofType(loadUserMasterFromBackend),
            switchMap(() => this.getUserDataFromBackend$()),
            switchMap((userModels) => {
                if (userModels.length !== 1) {
                    throw new Error('UserMasterEffects: More than one sales org/plant information!');
                }
                return this.finalizeSelection$(userModels[0]);
            }),
            catchError((error) => {
                this.log.error(`UserMasterEffects: applyHydratedOrResolveUserMaster$: ${error}`);
                return of(initUserMasterFailed({ hardFail: true, error: String(error) }));
            }),
        ),
    );

    /** Load the user master data and resolve the single expected user (hard-fail otherwise). */
    private getUserDataFromBackend$(): Observable<UserMasterState[]> {
        return this.onlineOnlyService.get<ODataUserModel>(ENDPOINTS.UserMasterSet).pipe(
            map((odataUsers: ODataUserModel[]) => {
                if (!odataUsers || odataUsers.length === 0) {
                    throw new Error('UserMasterEffects: could not load sales org/plant information!');
                }
                return odataUsers.map((odataUser) => ({
                    frontId: odataUser.FrontId,
                    country: odataUser.Country,
                    salesOrg: odataUser.SalesOrg,
                    plant: odataUser.Plant,
                    description: odataUser.Description,
                    emailId: odataUser.EmailId,
                    appId: this.environmentService.appId,
                    userRole: odataUser.UserRole,
                    personnelArea: odataUser.PersonnelArea,
                    personnelSubArea: odataUser.PersonnelSubarea,
                }));
            }),
        );
    }

    /**
     * Validate, enforce the partner/internal gate and announce the selection.
     *
     * @param selectedUser
     */
    private finalizeSelection$(selectedUser: UserMasterState): Observable<Action> {
        if (
            selectedUser.country.length === 0 ||
            selectedUser.plant.length === 0 ||
            selectedUser.salesOrg.length === 0
        ) {
            this.log.error(
                `UserMasterEffects: finalizeSelection$: the selected user model '${JSON.stringify(
                    selectedUser,
                )}' does not contain all the required fields`,
            );
            this.appNotificationService.setAppNotification(
                AppNotificationType.ERROR,
                <string>this.translate.instant('USER_MASTER_SET_MISSING_DATA_TITLE'),
                <string>this.translate.instant('USER_MASTER_SET_MISSING_DATA_BODY'),
            );
        }

        // For the FI app we use auth0 for internal and external users. On PACE for Partners an
        // internal (non-'Z') user is not allowed: show the error and hard-fail.
        if (
            !this.clientManager.isPaceFormsFI() &&
            this.environmentService.auth0Enabled &&
            !isPaceOnlineUserFrontId(selectedUser.frontId)
        ) {
            this.modalService.show(PacePartnerZgidErrorModalComponent, {
                backdrop: 'static',
                class: ModalClass.MODAL_MEDIUM,
                ariaLabelledBy: 'modal-title',
            });
            return of(
                initUserMasterFailed({
                    hardFail: true,
                    error: 'PACE for Partners is not accessible with an internal user account',
                }),
            );
        }

        this.applySelectionSideEffects(selectedUser);
        return of(userSelected({ user: selectedUser }));
    }

    /**
     * Bridge the selected country into the (largely static) ClientManager and report the user to
     * insights. Both live outside the store, so they have to be pushed rather than selected.
     *
     * @param user
     */
    private applySelectionSideEffects(user: UserMasterState): void {
        this.clientManager.userCountry = user.country;
        this.paceInsights.setUser(user.frontId, user.country, user.salesOrg, user.plant).catch(console.error);
    }
}
